import { useAuthStore } from '@/stores/auth.store';
import { useCommandPaletteStore } from '@/stores/command-palette.store';
import { useDensityStore } from '@/stores/density.store';
import { useWsStore } from '@/stores/ws.store';
import {
  Avatar,
  Button,
  Kbd,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  cn,
} from '@ominiflow/ui';
import { Link } from '@tanstack/react-router';
import { LogOut, Search, Settings, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../../../../../packages/ui/src/language-selector';
import { ThemeToggle } from '../../../../../packages/ui/src/theme-toggle';
import { SlaNotifications } from './sla-notifications';

export function Header() {
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const wsStatus = useWsStore((s) => s.status);
  const openPalette = useCommandPaletteStore((s) => s.setOpen);
  const { density, cycle: cycleDensity } = useDensityStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  function handleLogout() {
    logout();
    window.location.replace('/login');
  }

  const isConnected = wsStatus === 'connected';

  return (
    <header className="flex h-12 items-center justify-between border-b border-border bg-card/80 backdrop-blur-sm px-4 shrink-0">
      {/* Left: tenant workspace name & Global Search */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-foreground/80">
          {user?.tenantName ?? t('sidebar.user.workspaceFallback')}
        </span>

        <div className="relative hidden lg:block">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
          <button
            type="button"
            onClick={() => openPalette(true)}
            aria-label={t('header.quickSearchAria')}
            className="h-8 w-60 rounded-md border border-input/60 bg-muted/30 pl-8 pr-3 text-sm text-muted-foreground flex items-center justify-between hover:bg-accent/60 hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <span className="text-[12px]">{t('header.quickSearchPlaceholder')}</span>
            <Kbd>⌘K</Kbd>
          </button>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2">
        {/* WebSocket status pill */}
        <output
          aria-live="polite"
          aria-label={isConnected ? t('header.wsLiveAria') : t('header.wsReconnectingAria')}
          className={cn(
            'hidden sm:flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
            isConnected ? 'text-emerald-500' : 'text-amber-500',
          )}
          title={isConnected ? t('header.wsLiveAria') : t('header.wsReconnectingAria')}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500',
            )}
          />
          {isConnected ? t('ws.live') : t('ws.reconnecting')}
        </output>

        <div className="mx-1 h-5 w-px bg-border hidden sm:block" />

        <Button
          variant="ghost"
          size="icon"
          onClick={cycleDensity}
          aria-label={t('header.densityAria', { density })}
          title={t('header.densityTitle', { density })}
          className="hidden sm:flex text-muted-foreground/70 hover:text-foreground"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
        <SlaNotifications />
        <LanguageSelector />
        <ThemeToggle />

        <div className="mx-1 h-5 w-px bg-border" />

        {/* User menu — single avatar with Popover dropdown (H1-06 T3) */}
        <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-lg p-1 hover:bg-accent/70 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={t('header.userMenu')}
              title={t('header.userMenu')}
            >
              <Avatar name={user?.email ?? 'U'} size="sm" className="shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={6} className="w-52 p-1.5">
            <div className="px-2 py-1.5 mb-1">
              <p className="text-xs font-semibold text-foreground truncate">
                {user?.email?.split('@')[0] ?? t('sidebar.user.agentFallback')}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Separator className="my-1" />
            <Link
              to={'/settings/profile' as any}
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent/70 transition-colors w-full"
            >
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              {t('nav.settings')}
            </Link>
            <Separator className="my-1" />
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors w-full"
            >
              <LogOut className="h-3.5 w-3.5" />
              {t('user.logout')}
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}
